import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { AssetCategory, AssetCondition, AssetInput, type Asset } from '@syndiqia/maintenance/contracts';
import { errorMessage } from '../../api';
import { Alert, Disclosure, Empty, Field, Loading, toNumber, validate, type FieldErrors } from '../../components/ui';
import { useI18n } from '../../i18n';
import { useAssets, useMaintenanceMutation } from '../../maintenance-queries';
import { useSyndicate } from '../../session';
import { MaintenanceFrame, centsOf, dollars } from './MaintenanceNav';

export function AssetsPage() {
  const { t, date, money } = useI18n();
  const s = useSyndicate();
  const m = t.maintenance;
  const assets = useAssets();
  const [editing, setEditing] = useState<string | null>(null);
  const manages = s.can('maintenance.manage');
  const year = new Date().getFullYear();

  return (
    <MaintenanceFrame intro={m.assetsIntro} actions={manages && <Disclosure label={m.addAsset}>{(close) => <AssetForm onDone={close} />}</Disclosure>}>
      {assets.isPending && <Loading label={t.loading} />}
      {assets.isError && <Alert>{errorMessage(assets.error, t)}</Alert>}
      {assets.data?.length === 0 && <Empty>{m.noAssets}</Empty>}
      {!!assets.data?.length && (
        <table className="table table-stack">
          <thead>
            <tr>
              <th>{m.assetName}</th>
              <th>{m.category}</th>
              <th>{m.installedOn}</th>
              <th>{m.endOfLife}</th>
              <th>{m.condition}</th>
              <th className="num">{m.replacementCost}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {assets.data.map((a) =>
              editing === a.id ? (
                <tr key={a.id}>
                  <td colSpan={7}>
                    <AssetForm asset={a} onDone={() => setEditing(null)} />
                  </td>
                </tr>
              ) : (
                <tr key={a.id}>
                  <td className="cell-title">
                    {a.name}
                    {a.location && <div className="muted small">{a.location}</div>}
                  </td>
                  <td data-label={m.category}>{m.categories[a.category]}</td>
                  <td data-label={m.installedOn}>{a.installedOn ? date(a.installedOn) : m.none}</td>
                  <td data-label={m.endOfLife}>
                    {a.endOfLifeYear ? <span className={a.endOfLifeYear <= year + 5 ? 'text-warning' : undefined}>{a.endOfLifeYear}</span> : m.none}
                  </td>
                  <td data-label={m.condition}>
                    <span className={`badge badge-condition-${a.condition}`}>{m.conditions[a.condition]}</span>
                  </td>
                  <td data-label={m.replacementCost} className="num">
                    {a.replacementCostCents != null ? money(a.replacementCostCents) : m.none}
                  </td>
                  <td className="cell-actions">
                    <Link to={`${s.base}/maintenance/logbook?asset=${a.id}`} className="small">
                      {m.history}
                    </Link>
                    {manages && (
                      <button type="button" className="link-button small" onClick={() => setEditing(a.id)}>
                        {m.edit}
                      </button>
                    )}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      )}
    </MaintenanceFrame>
  );
}

function AssetForm({ asset, onDone }: { asset?: Asset; onDone: () => void }) {
  const { t } = useI18n();
  const m = t.maintenance;
  const [values, setValues] = useState({
    name: asset?.name ?? '',
    category: asset?.category ?? 'roofing',
    location: asset?.location ?? '',
    installedOn: asset?.installedOn ?? '',
    expectedLifeYears: asset?.expectedLifeYears?.toString() ?? '',
    replacementCost: dollars(asset?.replacementCostCents),
    condition: asset?.condition ?? 'unknown',
    notes: asset?.notes ?? '',
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const save = useMaintenanceMutation<AssetInput, Asset>((api, base, input) =>
    api(asset ? `${base}/assets/${asset.id}` : `${base}/assets`, { method: asset ? 'PUT' : 'POST', body: input }),
  );
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const { replacementCost, expectedLifeYears, ...rest } = values;
    const result = validate(AssetInput, { ...rest, expectedLifeYears: toNumber(expectedLifeYears), replacementCostCents: centsOf(replacementCost) });
    setErrors(result.errors ?? {});
    if (result.data) save.mutate(result.data, { onSuccess: onDone });
  };
  return (
    <form onSubmit={submit} className="form" noValidate>
      <div className="row">
        <Field label={m.assetName} error={errors.name}>
          <input value={values.name} onChange={set('name')} autoFocus />
        </Field>
        <Field label={m.category}>
          <select value={values.category} onChange={set('category')}>
            {AssetCategory.options.map((c) => (
              <option key={c} value={c}>
                {m.categories[c]}
              </option>
            ))}
          </select>
        </Field>
        <Field label={m.location} hint={t.common.optional}>
          <input value={values.location} onChange={set('location')} />
        </Field>
      </div>
      <div className="row">
        <Field label={m.installedOn} hint={t.common.optional} error={errors.installedOn}>
          <input type="date" value={values.installedOn} onChange={set('installedOn')} />
        </Field>
        <Field label={m.lifeYears} hint={t.common.optional} error={errors.expectedLifeYears}>
          <input inputMode="numeric" value={values.expectedLifeYears} onChange={set('expectedLifeYears')} />
        </Field>
        <Field label={m.replacementCost} hint={t.common.optional} error={errors.replacementCostCents}>
          <input inputMode="decimal" value={values.replacementCost} onChange={set('replacementCost')} placeholder="0,00 $" />
        </Field>
        <Field label={m.condition}>
          <select value={values.condition} onChange={set('condition')}>
            {AssetCondition.options.map((c) => (
              <option key={c} value={c}>
                {m.conditions[c]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label={m.notes} hint={t.common.optional}>
        <textarea rows={2} value={values.notes} onChange={set('notes')} />
      </Field>
      {save.isError && <Alert>{errorMessage(save.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={save.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}
